import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useFetch } from '../../../hooks';
import { useCart } from '../../../contexts/CartContext';
import { useToast } from '../../../contexts/ToastContext';
import { safeArray } from '../../../utils/helpers';
import Button from '../../ui/Button';

const PricingSection = ({ items }) => {
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const { showToast } = useToast();

  // If the CMS config provides items, keep them as a fallback. Default is dynamic plans.
  const fallbackPlans = Array.isArray(items) ? items : [];

  const { data: apiPlans } = useFetch('/public/plans', {
    cacheTtl: 0,
    defaultValue: [],
    transform: (data) => safeArray(data),
  });

  const plans = apiPlans.length > 0 ? apiPlans : fallbackPlans;

  const normalizedPlans = useMemo(
    () =>
      plans
        .filter((p) => (p?.status ? p.status === 'active' : true))
        .map((p, idx) => {
          const rawFeatures = Array.isArray(p.features)
            ? p.features
            : Array.isArray(p.items)
              ? p.items
              : typeof p.features === 'string'
                ? (() => {
                    try {
                      return JSON.parse(p.features || '[]');
                    } catch (e) {
                      return [];
                    }
                  })()
                : [];

          const features = rawFeatures
            .map((f) => {
              if (!f) return null;
              if (typeof f === 'string') {
                return { label: f, description: '' };
              }
              if (typeof f === 'object') {
                return { label: f.label || f.name || '', description: f.description || '' };
              }
              return null;
            })
            .filter((f) => f && f.label);

          const cadence =
            p.cadence ||
            (p.duration_months
              ? `/ ${Number(p.duration_months) === 1 ? 'month' : `${p.duration_months} months`}`
              : '/ month');

          const priceValue = p.price;
          const priceText =
            typeof priceValue === 'number'
              ? `KES ${priceValue.toLocaleString()}`
              : priceValue
                ? `KES ${Number(priceValue).toLocaleString()}`
                : p.price || '';

          return {
            id: p.id ?? p.plan_id ?? p.planId ?? idx,
            plan_id: p.plan_id ?? p.planId ?? p.id ?? null,
            name: p.name,
            description: p.description || p.blurb || '',
            featured: Boolean(p.featured) || idx === 1,
            cadence,
            priceText,
            rawPrice: p.price,
            features,
          };
        }),
    [plans]
  );

  const handleAddToCart = (uiPlan) => {
    const current = cartItems?.[0] || null;
    const numericId = uiPlan?.plan_id ? Number(uiPlan.plan_id) : null;
    const matchedApiPlan =
      numericId !== null ? apiPlans.find((p) => Number(p?.id) === numericId) || null : null;

    const toAdd = matchedApiPlan || {
      id: uiPlan?.plan_id ?? uiPlan?.id,
      name: uiPlan?.name,
      description: uiPlan?.description || '',
      price: typeof uiPlan?.rawPrice === 'number' ? uiPlan.rawPrice : Number(uiPlan?.rawPrice || 0),
      duration_months: 1,
      status: 'active',
    };

    addToCart(toAdd);

    if (current && Number(current.id) !== Number(toAdd?.id)) {
      showToast(`Plan changed to ${toAdd?.name || 'selected plan'}`, 'info');
    } else {
      showToast('Membership added to cart', 'success');
    }
    navigate('/subscription/cart', { state: { returnTo: '/#pricing' } });
  };

  const allFeatures = useMemo(() => {
    const map = new Map();
    normalizedPlans.forEach((p) => {
      p.features.forEach((f) => {
        const key = String(f.label).trim();
        if (!key) return;
        if (!map.has(key)) {
          map.set(key, { label: key, description: f.description || '' });
        } else if (!map.get(key).description && f.description) {
          map.set(key, { label: key, description: f.description });
        }
      });
    });
    return Array.from(map.values());
  }, [normalizedPlans]);

  return (
    <section
      className="px-6 py-20 md:py-28 bg-gradient-to-b from-[#0f1115] to-[#131820]"
      id="pricing"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-[#f8f5ed] mb-4">Pricing</h2>
          <p className="text-lg text-[#d8d2c5] max-w-2xl mx-auto">
            Start small. Upgrade when your group is ready.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {normalizedPlans.map((plan) => (
            <div
              key={`plan-${plan.id ?? plan.plan_id ?? plan.planId ?? plan.name ?? plan.priceText}`}
              className={`rounded-2xl border bg-white/5 p-6 backdrop-blur ${
                plan.featured ? 'border-emerald-400/40' : 'border-white/10'
              }`}
            >
              <div className="text-xl font-semibold text-[#f8f5ed]">{plan.name}</div>
              <div className="mt-4 flex items-end gap-2">
                <div className="text-4xl font-bold text-emerald-200">{plan.priceText}</div>
                <div className="text-sm text-[#d8d2c5]/80">{plan.cadence}</div>
              </div>
              <div className="mt-3 text-sm text-[#d8d2c5]/85">{plan.description}</div>
              <ul className="mt-6 space-y-2 text-sm text-[#d8d2c5]">
                {(plan.features || []).map((it) => (
                  <li
                    key={`plan-${plan.id ?? plan.plan_id ?? plan.planId ?? plan.name ?? plan.priceText}-item-${it.label}`}
                    className="flex gap-2"
                    title={it.description || ''}
                  >
                    <span className="text-emerald-300">•</span>
                    <span>{it.label}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <Button fullWidth onClick={() => handleAddToCart(plan)}>
                  Choose Plan
                </Button>
              </div>
            </div>
          ))}
        </div>

        {normalizedPlans.length > 0 && allFeatures.length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-[#f8f5ed] mb-4">Compare features</h3>
            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/5 backdrop-blur">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-[#f8f5ed]">Feature</th>
                    {normalizedPlans.map((p) => (
                      <th key={`cmp-${p.id}`} className="px-6 py-4 text-left text-[#f8f5ed]">
                        {p.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allFeatures.map((f) => (
                    <tr
                      key={`cmp-row-${f.label}`}
                      className="border-b border-white/10 last:border-0"
                    >
                      <td className="px-6 py-4 text-[#d8d2c5]" title={f.description || ''}>
                        {f.label}
                      </td>
                      {normalizedPlans.map((p) => {
                        const has = p.features.some((pf) => String(pf.label).trim() === f.label);
                        return (
                          <td key={`cmp-cell-${p.id}-${f.label}`} className="px-6 py-4">
                            {has ? (
                              <span className="text-emerald-300" aria-label="Included">
                                ✓
                              </span>
                            ) : (
                              <span className="text-[#d8d2c5]/40" aria-label="Not included">
                                —
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

PricingSection.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cadence: PropTypes.string,
      blurb: PropTypes.string,
      featured: PropTypes.bool,
      items: PropTypes.arrayOf(PropTypes.string),
      plan_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      planId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

PricingSection.defaultProps = {
  items: [],
};

export default PricingSection;
